import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { fetchCampaignDetail, fetchThread, fetchThreads, sendMessage } from "./services/brandModeServices";
import { fullName } from "./brandUtils";
import { fmtDateTime, timeAgo } from "./brandDealUtils";
import iconPaperclip from "../../assets/brand/icon-paperclip.svg";
import {
  BackLink,
  Badge,
  Btn,
  CampaignContextBar,
  Card,
  ErrorState,
  Overline,
} from "./ui/BrandUI";

const offerHref = (deal) => {
  if (!deal) return null;
  if (deal.status === "countered") return `/brand/deals/${deal.id}/counter`;
  if (deal.status === "draft") return `/brand/campaigns/${deal.campaignId}/offer/${deal.athleteId}`;
  return `/brand/deals/${deal.id}`;
};

const Bubble = ({ message }) => {
  const mine = message.from === "brand";
  return (
    <div className={`flex flex-col gap-[6px] ${mine ? "items-end" : "items-start"}`}>
      <div
        className={`max-w-[520px] px-4 py-3 bm-body-m text-bm-ink ${
          mine ? "rounded-[12px] rounded-br-[4px] bg-bm-mutedbg" : "rounded-[12px] rounded-bl-[4px] border border-bm-border bg-white"
        }`}
      >
        {message.text}
        {message.attachment?.name && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-bm-border bg-white px-3 py-1 bm-label-xs text-bm-ink">
            <img src={iconPaperclip} alt="" className="h-3 w-3" />
            {message.attachment.name}
          </div>
        )}
      </div>
      <span className="bm-body-s text-bm-muted">
        {mine ? "You" : message.senderName || "Athlete"} · {fmtDateTime(message.at)}
      </span>
    </div>
  );
};

const MessagesPage = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [params, setParams] = useSearchParams();
  const { refreshBadges } = useBrandMode();
  const fileRef = useRef(null);
  const viewportRef = useRef(null);

  const [threads, setThreads] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [athleteCount, setAthleteCount] = useState(0);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);

  const threadId = params.get("thread");

  const loadThreads = async () => {
    try {
      const data = await fetchThreads(campaignId);
      setThreads(data.threads || []);
      if (!threadId && data.threads?.length) setParams({ thread: data.threads[0].id }, { replace: true });
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load conversations");
    }
  };

  const loadThread = async (id) => {
    if (!id) return;
    try {
      const data = await fetchThread(id);
      setActive(data);
      setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unread: 0 } : t)));
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    }
  };

  useEffect(() => {
    loadThreads();
    if (campaignId) {
      fetchCampaignDetail(campaignId)
        .then((d) => {
          setCampaign(d.campaign);
          setAthleteCount(d.roster?.length || 0);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    loadThread(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Light polling keeps athlete replies flowing in while the page is open.
  useEffect(() => {
    const timer = setInterval(() => {
      loadThreads();
      if (threadId) loadThread(threadId);
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, campaignId]);

  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
  }, [active?.messages?.length]);

  const athleteName = useMemo(() => fullName(active?.athlete), [active]);

  const submit = async () => {
    if (!text.trim() && !attachment) return;
    setSending(true);
    try {
      await sendMessage(threadId, { text: text.trim(), attachment });
      setText("");
      setAttachment(null);
      await loadThread(threadId);
      await loadThreads();
    } catch (err) {
      toast.error(err.message || "Could not send the message");
    } finally {
      setSending(false);
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setAttachment({ name: f.name, size: f.size });
    e.target.value = "";
  };

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to={campaignId ? `/brand/campaigns/${campaignId}` : "/brand/home"}>
        {campaignId ? "Back to campaign" : "Back to home"}
      </BackLink>
      {campaign && (
        <CampaignContextBar
          campaign={campaign}
          linkTo={`/brand/campaigns/${campaignId}`}
          right={<span>{`${athleteCount} athletes  ·  ${threads.length} conversation${threads.length === 1 ? "" : "s"}`}</span>}
        />
      )}

      <div className="flex flex-col gap-[6px]">
        <Overline brand>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">Messages</h1>
        <p className="bm-body-l text-bm-muted">All conversations in one place · reps have thread visibility</p>
      </div>

      {error && <ErrorState>{error}</ErrorState>}

      <Card padding="p-0" className="flex h-[680px] overflow-hidden">
        {/* Conversation list */}
        <div className="flex w-[300px] shrink-0 flex-col border-r border-bm-border bg-bm-subtle md:w-[360px]">
          <div className="border-b border-bm-mutedbg p-4 bm-h-xs text-bm-ink">Conversations</div>
          <div className="bm-scroll flex-1 overflow-y-auto">
            {threads.length === 0 && (
              <p className="p-4 bm-body-m text-bm-muted">
                No conversations yet. Sending an offer or a message from a campaign starts one.
              </p>
            )}
            {threads.map((t) => {
              const selected = t.id === threadId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setParams({ thread: t.id })}
                  className={`flex w-full flex-col gap-1 border-b p-4 text-left ${selected ? "border-bm-border bg-white" : "border-transparent hover:bg-white/60"}`}
                >
                  <span className="flex items-center justify-between">
                    <span className="bm-label-l text-bm-ink">{fullName(t.athlete) || "Athlete"}</span>
                    <span className="bm-body-s text-bm-tertiary">{timeAgo(t.lastAt, { short: true })} ago</span>
                  </span>
                  {!campaignId && t.campaignName && <span className="bm-body-s text-bm-tertiary">{t.campaignName}</span>}
                  <span className={`line-clamp-2 bm-body-m ${t.unread ? "text-bm-ink" : "text-bm-muted"}`}>{t.preview || "No messages yet"}</span>
                  {t.unread > 0 && (
                    <span className="pt-1">
                      <Badge tone="brand">New</Badge>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Thread */}
        <div className="flex min-w-0 flex-1 flex-col">
          {active ? (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-bm-border p-4">
                <span className="flex flex-col gap-[2px]">
                  <span className="bm-h-m text-bm-ink">{athleteName || "Athlete"}</span>
                  <span className="bm-body-s text-bm-muted">
                    {[active.athlete?.position, active.athlete?.school, active.campaign?.name].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {offerHref(active.deal) && (
                  <Btn variant="secondary" size="sm" onClick={() => navigate(offerHref(active.deal))}>
                    View offer
                  </Btn>
                )}
              </div>
              <div ref={viewportRef} className="bm-scroll flex flex-1 flex-col gap-4 overflow-y-auto p-6">
                {active.messages?.length ? (
                  active.messages.map((m) => <Bubble key={m.id} message={{ ...m, senderName: athleteName }} />)
                ) : (
                  <p className="bm-body-m text-bm-muted">Say hello to start the conversation.</p>
                )}
              </div>
              <div className="flex flex-col gap-3 border-t border-bm-border bg-bm-subtle p-4">
                <div className="flex items-center gap-3">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), submit())}
                    placeholder={`Reply to ${active.athlete?.name || "athlete"}...`}
                    className="bm-input flex-1"
                  />
                  <input ref={fileRef} type="file" className="hidden" onChange={pickFile} />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    aria-label="Attach a file"
                    title={attachment ? attachment.name : "Attach a file"}
                    className={`flex h-10 w-10 items-center justify-center rounded-full border ${attachment ? "border-bm-orange bg-white" : "border-bm-border bg-bm-subtle"}`}
                  >
                    <img src={iconPaperclip} alt="" className="h-4 w-4" />
                  </button>
                  <Btn variant="tertiary" onClick={submit} disabled={sending || (!text.trim() && !attachment)}>
                    {sending ? "Sending..." : "Send"}
                  </Btn>
                </div>
                {attachment && (
                  <span className="flex items-center gap-2 bm-body-s text-bm-muted">
                    Attaching {attachment.name}
                    <button type="button" onClick={() => setAttachment(null)} className="text-bm-critical">remove</button>
                  </span>
                )}
                <p className="bm-body-s text-bm-muted">
                  Messages are delivered in-app. Athletes also get an email notification for every new message.
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center bm-body-m text-bm-muted">
              {threads.length ? "Pick a conversation." : "No conversation selected."}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default MessagesPage;
